import { LocalStorageKeys } from "@/common/constants";
import authApi from "@/types/auth/authApi";
import { callApi } from "@/Utils/request/query";
import { HTTPError } from "@/Utils/request/types";
import { suspendSession } from "./sessionRecovery";

let pending: Promise<void> | undefined;
export const invalidSession = (error: unknown) =>
  error instanceof HTTPError && (error.status === 401 || error.status === 403);

/** Read the rotating token inside the cross-tab lock, never from a render closure.
 * No clinical command is replayed here. Uncertain refresh outcomes keep tokens.
 */
export function refreshSession(): Promise<void> {
  if (pending) return pending;
  const accessAtStart = localStorage.getItem(LocalStorageKeys.accessToken);
  const refresh = async () => {
    if (localStorage.getItem(LocalStorageKeys.accessToken) !== accessAtStart)
      return;
    const token = localStorage.getItem(LocalStorageKeys.refreshToken);
    if (!token) {
      suspendSession();
      return;
    }
    const result = await callApi(authApi.tokenRefresh, {
      body: { refresh: token },
      silent: true,
      signal: AbortSignal.timeout(15000),
    });
    // A sign-out or another login during this request must win.
    if (localStorage.getItem(LocalStorageKeys.refreshToken) !== token) return;
    if (
      typeof result.access !== "string" ||
      typeof result.refresh !== "string" ||
      !result.access ||
      !result.refresh
    )
      throw new Error("Invalid token response");
    localStorage.setItem(LocalStorageKeys.refreshToken, result.refresh);
    localStorage.setItem(LocalStorageKeys.accessToken, result.access);
  };
  pending = (
    navigator.locks
      ? navigator.locks.request(
          "care-session-refresh",
          { signal: AbortSignal.timeout(20000) },
          refresh,
        )
      : refresh()
  )
    .catch((error: unknown) => {
      if (
        invalidSession(error) &&
        localStorage.getItem(LocalStorageKeys.accessToken) === accessAtStart
      )
        suspendSession();
      throw error;
    })
    .finally(() => {
      pending = undefined;
    });
  return pending;
}
